import React, { useState } from 'react';
import { Calculator as CalcIcon, Clock, Layers, ArrowRight, Sparkles } from 'lucide-react';

const Calculator = ({ onOpenContact }) => {
  const [surveyType, setSurveyType] = useState('dgps');
  const [landArea, setLandArea] = useState(10);
  const [unit, setUnit] = useState('acres');
  const [terrain, setTerrain] = useState('flat');
  const [location, setLocation] = useState('theni');

  const calculateEstimate = () => {
    // 1. Calculate acreage
    let acres = unit === 'acres' ? landArea : landArea / 43560;

    // 2. Base Fee Formula: ₹15,000 Base + ₹3,000 per Acre
    const BASE_FEE = 15000;
    const RATE_PER_ACRE = 3000;
    let baseTotal = BASE_FEE + (acres * RATE_PER_ACRE);

    // 3. Work Type Multipliers
    let typeMult = 1.0;
    if (surveyType === 'wrd') typeMult = 1.15;
    if (surveyType === 'highways') typeMult = 1.2;
    if (surveyType === 'topographical') typeMult = 1.1;

    // 4. Terrain Multipliers
    let terrainMult = 1.0;
    if (terrain === 'undulated') terrainMult = 1.15;
    if (terrain === 'dense') terrainMult = 1.3;

    // 5. Location Multipliers
    let locMult = 1.0;
    if (location === 'madurai') locMult = 1.05;
    if (location === 'tn') locMult = 1.15;

    let calculatedTotal = Math.round(baseTotal * typeMult * terrainMult * locMult);

    // 6. Range Calculation (±10%)
    let estMin = Math.round(calculatedTotal * 0.95);
    let estMax = Math.round(calculatedTotal * 1.10);

    // Fieldwork time estimation
    let days = Math.ceil(acres / 8);
    if (days < 1) days = 1;

    return {
      estMin,
      estMax,
      days,
      deliveryDays: days + 2
    };
  };

  const est = calculateEstimate();

  return (
    <section id="calculator" className="section-padding" style={{ position: 'relative', background: '#f8fafc' }}>
      <div className="container">
        {/* Section Title */}
        <div className="section-title-wrapper">
          <div className="section-subtitle">Instant Projection</div>
          <h2 className="section-title">
            Interactive <span className="gradient-text">Survey Cost Estimator</span>
          </h2>
          <p className="section-desc">
            Calculate instant budget ranges, timeframe projections, and recommended DGPS field equipment tailored to your land extent.
          </p>
        </div>

        {/* Estimator Card Container */}
        <div
          className="glass-card"
          style={{
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '2.5rem',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 20px 40px -10px rgba(15, 23, 42, 0.08)',
          }}
        >
          <div className="grid-2" style={{ gap: '3rem', alignItems: 'center' }}>
            {/* Input Controls */}
            <div>
              {/* 1. Select Survey Category */}
              <div style={{ marginBottom: '1.75rem' }}>
                <label style={{ display: 'block', color: '#0f172a', fontWeight: '700', marginBottom: '0.6rem', fontSize: '0.95rem' }}>
                  1. Select Survey Work Type:
                </label>
                <select
                  value={surveyType}
                  onChange={(e) => setSurveyType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.85rem 1rem',
                    borderRadius: '10px',
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    color: '#0f172a',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.95rem',
                    outline: 'none',
                    cursor: 'pointer',
                    fontWeight: '600',
                  }}
                >
                  <option value="dgps">DGPS High-Accuracy Survey</option>
                  <option value="boundary">Boundary Survey & Land Details</option>
                  <option value="wrd">Water Resources Development (W.R.D. Work)</option>
                  <option value="highways">Highways, Road Alignment & Volumetric Calculations</option>
                  <option value="topographical">Topographical & Contour Survey</option>
                </select>
              </div>

              {/* 2. Land Area Input & Unit Switcher */}
              <div style={{ marginBottom: '1.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                  <label style={{ color: '#0f172a', fontWeight: '700', fontSize: '0.95rem' }}>
                    2. Approximate Site Size:
                  </label>
                  <div style={{ display: 'flex', gap: '0.25rem', background: '#f1f5f9', padding: '0.2rem', borderRadius: '8px' }}>
                    <button
                      onClick={() => { setUnit('acres'); setLandArea(10); }}
                      style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        border: 'none',
                        background: unit === 'acres' ? '#e63946' : 'transparent',
                        color: unit === 'acres' ? '#ffffff' : '#475569',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                      }}
                    >
                      Acres
                    </button>
                    <button
                      onClick={() => { setUnit('sqft'); setLandArea(25000); }}
                      style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        border: 'none',
                        background: unit === 'sqft' ? '#e63946' : 'transparent',
                        color: unit === 'sqft' ? '#ffffff' : '#475569',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                      }}
                    >
                      Sq. Ft.
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                  <input
                    type="range"
                    min={unit === 'acres' ? 1 : 2000}
                    max={unit === 'acres' ? 100 : 500000}
                    step={unit === 'acres' ? 1 : 1000}
                    value={landArea}
                    onChange={(e) => setLandArea(Number(e.target.value))}
                    style={{ flex: 1, accentColor: '#e63946', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '800', fontSize: '1.1rem', color: '#e63946', minWidth: '95px', textAlign: 'right' }}>
                    {landArea.toLocaleString()} {unit === 'acres' ? 'Acres' : 'Sq Ft'}
                  </span>
                </div>
              </div>

              {/* 3. Terrain Profile */}
              <div style={{ marginBottom: '1.75rem' }}>
                <label style={{ display: 'block', color: '#0f172a', fontWeight: '700', marginBottom: '0.6rem', fontSize: '0.95rem' }}>
                  3. Terrain Profile:
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                  {[
                    { id: 'flat', label: 'Flat / Clear' },
                    { id: 'undulated', label: 'Undulated' },
                    { id: 'dense', label: 'Dense / Hilly' }
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTerrain(t.id)}
                      style={{
                        padding: '0.65rem 0.5rem',
                        borderRadius: '8px',
                        border: terrain === t.id ? '1px solid #e63946' : '1px solid #cbd5e1',
                        background: terrain === t.id ? '#fff5f5' : '#ffffff',
                        color: terrain === t.id ? '#e63946' : '#475569',
                        fontSize: '0.825rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 4. Location Region */}
              <div>
                <label style={{ display: 'block', color: '#0f172a', fontWeight: '700', marginBottom: '0.6rem', fontSize: '0.95rem' }}>
                  4. Location Region:
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                  {[
                    { id: 'theni', label: 'Theni District' },
                    { id: 'madurai', label: 'Madurai Dt' },
                    { id: 'tn', label: 'Rest of TN' }
                  ].map((loc) => (
                    <button
                      key={loc.id}
                      onClick={() => setLocation(loc.id)}
                      style={{
                        padding: '0.65rem 0.5rem',
                        borderRadius: '8px',
                        border: location === loc.id ? '1px solid #0066ff' : '1px solid #cbd5e1',
                        background: location === loc.id ? '#eff6ff' : '#ffffff',
                        color: location === loc.id ? '#0066ff' : '#475569',
                        fontSize: '0.825rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {loc.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results Display Panel */}
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '16px',
                padding: '2rem',
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <div className="badge badge-gold" style={{ marginBottom: '1.25rem' }}>
                <Sparkles size={13} /> Projected Quotation Range
              </div>

              {/* Price Range Output */}
              <div style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)', fontWeight: '800', fontFamily: 'var(--font-heading)', color: '#0f172a', lineHeight: 1.1 }}>
                ₹{est.estMin.toLocaleString()} – ₹{est.estMax.toLocaleString()}
              </div>

              {/* Pricing Rules Box */}
              <div style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '1rem', marginTop: '1rem', marginBottom: '1.5rem', textAlign: 'left' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0f172a', marginBottom: '0.4rem' }}>
                  Calculation Formula:
                </div>
                <div style={{ fontSize: '0.825rem', color: '#334155', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#e63946', fontWeight: '800' }}>✓</span> Base Fee: <strong>₹15,000</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: '#e63946', fontWeight: '800' }}>✓</span> Area Rate: <strong>+ ₹3,000 for every acre</strong>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: '600', marginTop: '0.2rem', fontStyle: 'italic' }}>
                    *Note: "Payment may change depending on the location."
                  </div>
                </div>
              </div>

              {/* Metrics Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem', textAlign: 'left' }}>
                <div style={{ background: '#ffffff', padding: '0.9rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#e63946', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.2rem' }}>
                    <Clock size={14} /> Fieldwork Time
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0f172a' }}>
                    ~{est.days} Field Day{est.days > 1 ? 's' : ''}
                  </div>
                </div>

                <div style={{ background: '#ffffff', padding: '0.9rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#059669', fontSize: '0.8rem', fontWeight: '700', marginBottom: '0.2rem' }}>
                    <Layers size={14} /> CAD Delivery
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0f172a' }}>
                    Within {est.deliveryDays} Days
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={() =>
                  onOpenContact(
                    `Estimator Request: ${surveyType.toUpperCase()} for ${landArea} ${unit} (${terrain} terrain in ${location})`
                  )
                }
                className="btn-primary btn-glow"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Lock In Estimate & Request Proposal <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
